import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import {
  IFormJBPHPContactChangeRequest,
  JBPHPContactChangeRequestStatus,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { FormInput, resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Grid, Item } from 'core-components/library/grid'
import { Input } from 'core-components/library/inputs'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

import { HP_CONTACT_REQUEST_EDITOR_FORM } from '../../config'
import { getAppUsersAppsSearchCrud, getCleanEmail } from '../../lib'
import { IChangeRequestFormData, mapFormData } from '../../lib/mapFormData'
import { AssignmentPeriodDropdown } from '../assignmentPeriodDropdown'
import { BusinessUnitMultiSelect } from '../businessUnitMultiSelect'
import { EmailField } from '../emailField'
import { ManagersSearch } from '../managersSearch'
import { RoleSelectionDropdown } from '../roleSelectionDropdown'

import styles from './HPContactRequestEditor.module.scss'

interface IProps {
  isVisible?: boolean
  shouldKeepCurrentUser?: boolean
  currentHpContactRequest: IFormJBPHPContactChangeRequest
  partnerId: string
  partnerName?: string
  leadId: number
  currentRole: string
  onApply: (item: IFormJBPHPContactChangeRequest) => Promise<void>
  onCancel: () => void
  isRequestAccess?: boolean
}

export const HPContactRequestEditor = ({
  isVisible = false,
  currentHpContactRequest: initialState,
  ...props
}: IProps) => {
  const [currentPbmEmail, setCurrentPbmEmail] = useState('')

  const mappedInitialState = useMemo(() => {
    return mapFormData(initialState)
  }, [initialState])

  const [{ formData, modifiedFields, isValid }, handleChange] = useGetFormData(
    HP_CONTACT_REQUEST_EDITOR_FORM,
    mappedInitialState
  )

  const isRequestChanged = modifiedFields.length > 0
  const dispatch = useAppDispatch()
  const appId = useSelector(selectAppId)
  const jbpPBMManagersCrud = CrudStore.get(CrudStoreItemPartner360.JBPPBMManagers)

  const onChangeContact = (newValue: string | number, fieldName: keyof IChangeRequestFormData) => {
    handleChange({
      field: fieldName,
      value: newValue,
    })
  }

  const onChangeEmail = (newValue: string, emailType: 'Contact' | 'Manager') => {
    const cleanEmail = getCleanEmail(newValue)
    if (emailType === 'Contact') {
      onChangeContact(cleanEmail, 'Email')
      setCurrentPbmEmail(cleanEmail)
    } else {
      onChangeContact(cleanEmail, 'ManagerEmail')
    }
  }

  const userCrud = useMemo(() => {
    const crud = CrudStore.get(CrudStoreItem.AppUsersAppsSearch, 'Users')
    crud.setDefaultFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'Id',
          operator: Operator.CONTAINS,
          value: '@hp.com',
        },
        {
          field: 'AppId',
          operator: Operator.EQUAL,
          value: appId,
        },
      ],
    })

    return crud
  }, [appId])

  const clearFilters = useCallback(() => {
    getAppUsersAppsSearchCrud({ appId }).setFilter({ logic: Logic.AND, filters: [] })
    userCrud.setFilter({ logic: Logic.AND, filters: [] })
  }, [userCrud, appId])

  const fetchManager = async () => {
    const result = await jbpPBMManagersCrud.fetch()
    if (result.items && result.items.length > 0) {
      const managerEmail = result.items[0].ManagerEmail
      if (managerEmail) {
        onChangeEmail(managerEmail, 'Manager')
      }
    }
  }

  useEffect(() => {
    if (requestAction === 'Add' && currentPbmEmail) {
      jbpPBMManagersCrud.setFilter({
        logic: Logic.AND,
        filters: [
          {
            field: 'PBMEmail',
            operator: Operator.EQUAL,
            value: currentPbmEmail,
          },
        ],
      })
      fetchManager()
    }
  }, [currentPbmEmail])

  const handleApply = async () => {
    let newRequest = {
      ...formData,

      PartnerId: props.partnerId,
      Email: formData.Email,
      FirstName: formData.FirstName,
      LastName: formData.LastName,
      BusinessUnit: formData.BusinessUnit.join('/'),
      Roles: formData.Roles,
      LeadPBM: formData.LeadPBM,

      ManagerEmail: formData.ManagerEmail,
      ManagerFirstName: formData.ManagerFirstName,
      ManagerLastName: formData.ManagerLastName,

      TicketAction: requestAction,

      RequestAction: requestAction,
    }

    if (!formData.TicketId || formData.TicketId === -1) {
      newRequest = {
        ...newRequest,
        TicketStatus: JBPHPContactChangeRequestStatus.New,
      }
    } else {
      newRequest = {
        ...newRequest,
        TicketStatus: JBPHPContactChangeRequestStatus.Update,
      }
    }

    clearFilters()
    await props.onApply(newRequest)

    dispatch(resetAllModifiedDataByKey(HP_CONTACT_REQUEST_EDITOR_FORM))
  }

  useEffect(() => {
    if (!isVisible) {
      dispatch(resetAllModifiedDataByKey(HP_CONTACT_REQUEST_EDITOR_FORM))
    }
  }, [dispatch, isVisible])

  const isTicketStatusNew =
    formData.TicketId > 0 && formData.TicketStatus === JBPHPContactChangeRequestStatus.New
  const isTicketStatusInProgress =
    formData.TicketId > 0 && formData.TicketStatus !== JBPHPContactChangeRequestStatus.New
  const requestAction = formData.RequestAction

  const isDeleteAction = requestAction === 'Delete'
  const isDeleteActionWithoutManagerEmail = isDeleteAction && !formData.ManagerEmail

  const isDisabledApply =
    isTicketStatusInProgress ||
    !formData.AssignmentPeriod ||
    !formData.Email ||
    !requestAction ||
    isDeleteActionWithoutManagerEmail ||
    (!isDeleteAction && (!isRequestChanged || !formData.Roles || !isValid))

  return (
    <div className={styles.root}>
      {props.isRequestAccess ? (
        <span className={styles.title}>
          <Translation text="jbp-request-access-title" span />
          {props.partnerId}
        </span>
      ) : (
        <div className={styles.title}>
          <Translation
            span
            htmlAvailable
            text="jbp-request-action-title"
            replaceParams={[
              {
                key: '{requestAction}',
                value: requestAction,
              },
            ]}
          />
        </div>
      )}
      {isTicketStatusNew && <div>You already have a request in status New. It can be edited.</div>}
      {isTicketStatusInProgress && (
        <div>You already have a request with a status of In Progress.</div>
      )}

      <Grid>
        {props.partnerName && (
          <Item xs={12} className={styles.item}>
            <Input type="text" label={'Partner Name'} value={props.partnerName} disabled={true} />
          </Item>
        )}
        <Item xs={12} className={styles.item}>
          <Input type="text" label={'Partner ID'} value={props.partnerId} disabled={true} />
        </Item>
        <Item xs={12} className={styles.item}>
          {requestAction !== 'Delete' && !isTicketStatusInProgress ? (
            <RoleSelectionDropdown
              defaultValue={mappedInitialState.Roles}
              isRequestAccess={props.isRequestAccess}
              disabled={requestAction === 'Delete' || isTicketStatusInProgress}
            />
          ) : (
            <Input value={formData.Roles} label={'Roles'} disabled />
          )}
        </Item>
        <Item xs={12} className={styles.item}>
          <BusinessUnitMultiSelect
            defaultValue={mappedInitialState.BusinessUnit}
            popPaperClassName={styles.rolePaper}
            disabled={requestAction === 'Delete' || isTicketStatusInProgress}
          />
        </Item>
        <Item xs={12} className={styles.item}>
          <EmailField
            isRequestAccess={props.isRequestAccess}
            requestAction={requestAction}
            email={mappedInitialState.Email || ''}
            userCrud={userCrud}
            isTicketStatusInProgress={isTicketStatusInProgress}
            shouldKeepCurrentUser={props.shouldKeepCurrentUser}
          />
        </Item>
        <Item xs={12} className={styles.item}>
          <FormInput
            formKey={HP_CONTACT_REQUEST_EDITOR_FORM}
            field="FirstName"
            type="text"
            label="First Name *"
            defaultValue={mappedInitialState.FirstName || ''}
            onValidate={async (value: string) => (value.length ? '' : 'Please, enter a value')}
            disabled={
              requestAction !== 'Add' ||
              props.isRequestAccess ||
              isTicketStatusInProgress ||
              props.shouldKeepCurrentUser
            }
          />
        </Item>
        <Item xs={12} className={styles.item}>
          <FormInput
            formKey={HP_CONTACT_REQUEST_EDITOR_FORM}
            field="LastName"
            type="text"
            label="Last Name *"
            defaultValue={mappedInitialState.LastName || ''}
            onValidate={(value: string) => ValidationHelper.checkString(value)}
            disabled={
              requestAction !== 'Add' ||
              props.isRequestAccess ||
              isTicketStatusInProgress ||
              props.shouldKeepCurrentUser
            }
          />
        </Item>
        <Item xs={12} className={styles.item}>
          <AssignmentPeriodDropdown value={mappedInitialState.AssignmentPeriod || ''} />
        </Item>
        <Item xs={12} className={styles.item}>
          <Checkbox
            label={<Translation text="jbp-hpcontactinfo-leadpbm" />}
            checked={!!formData.LeadPBM}
            onChange={() => onChangeContact(formData.LeadPBM ? 0 : 1, 'LeadPBM')}
            disabled={
              requestAction === 'Delete' ||
              isTicketStatusInProgress ||
              (props.leadId !== -1 && props.leadId !== (formData.EntryId || 0))
            }
          />
        </Item>
        <Item xs={12} className={styles.item}>
          <hr className={styles.line} />
        </Item>
        <Item xs={12} className={styles.item}>
          <ManagersSearch
            label={'Manager of this Contact *'}
            email={mappedInitialState.ManagerEmail || ''}
            checkFn={(value) => ValidationHelper.checkFullEmail(value, ['@hp.com'])}
          />
        </Item>
      </Grid>

      <div className={styles.buttons}>
        <Button
          onClick={async () => {
            clearFilters()
            props.onCancel()
          }}
          color="white"
        >
          Cancel
        </Button>
        <Button className={styles.applyButton} disabled={isDisabledApply} onClick={handleApply}>
          Confirm
        </Button>
      </div>
    </div>
  )
}
